import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, TrendingUp, Sparkles, Monitor } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { errMsg, IG_PROFILE } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const FIELDS = [
  { k: "first_name", l: "İsim" },
  { k: "last_name", l: "Soyisim" },
  { k: "phone", l: "Telefon Numarası", t: "tel", ph: "05xx xxx xx xx" },
  { k: "username", l: "Kullanıcı Adı" },
  { k: "password", l: "Şifre", t: "password", ph: "En az 6 karakter" },
];

function Brand() {
  return (
    <div className="relative hidden overflow-hidden bg-gradient-to-br from-cyan-600 via-teal-700 to-blue-950 p-12 text-white lg:flex lg:flex-col lg:justify-between">
      <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-cyan-300/20 blur-3xl" />
      <div className="absolute -bottom-32 -left-16 h-96 w-96 rounded-full bg-blue-400/20 blur-3xl" />
      <div className="relative font-heading text-2xl font-bold tracking-tight">Kasam Net</div>
      <div className="relative space-y-6">
        <h1 className="font-heading text-5xl font-extrabold leading-[1.05] tracking-tight">Kasanız her gün<br />kuruşu kuruşuna.</h1>
        <p className="max-w-md text-cyan-50/90">Günlük girdi-çıktı, kasa sayımı, açık/fazla takibi ve PDF raporlar. Birden fazla işletmeyi tek hesaptan yönetin.</p>
        <div className="space-y-3 text-sm">
          {[[TrendingUp, "Anlık grafikler ve aylık ciro"], [ShieldCheck, "Kasa açığı kırmızı, fazlası yeşil"], [Sparkles, "Yapay zeka finans asistanı"]].map(([I, t]) => (
            <div key={t} className="flex items-center gap-3"><I className="h-5 w-5 text-cyan-200" /> {t}</div>
          ))}
        </div>
      </div>
      <div className="relative text-xs text-cyan-100/70">Kayıt olanlara 1 ay ücretsiz lisans</div>
    </div>
  );
}

export default function AuthPage({ mode }) {
  const isReg = mode === "register";
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const fields = isReg ? FIELDS : FIELDS.slice(3);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isReg) await register(form);
      else await login(form.username || "", form.password || "");
      toast.success(isReg ? "Kayıt başarılı! 1 aylık lisansınız tanımlandı." : "Hoş geldiniz!");
      nav("/");
    } catch (err) {
      toast.error(errMsg(err));
    }
    setBusy(false);
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <Brand />
      <div className="flex items-center justify-center bg-slate-50 p-6">
        <form onSubmit={submit} className="w-full max-w-md space-y-5 rounded-3xl border border-slate-200 bg-white p-8 shadow-sm" data-testid={isReg ? "register-form" : "login-form"}>
          <img src="/img/kasamnet-logo.jpg" alt="Kasam Net" className="mx-auto h-28 object-contain mix-blend-multiply" />
          <div className="text-center">
            <h2 className="font-heading text-2xl font-bold text-slate-900">{isReg ? "Hesap Oluştur" : "Giriş Yap"}</h2>
            <p className="text-sm text-slate-500">{isReg ? "Kayıt olun, 1 ay ücretsiz kullanın." : "Hesabınıza giriş yapın."}</p>
          </div>
          <div className={`grid gap-4 ${isReg ? "sm:grid-cols-2" : ""}`}>
            {fields.map((f) => (
              <div key={f.k} className={isReg && f.k === "phone" ? "sm:col-span-2" : ""}>
                <Label htmlFor={f.k} className="text-slate-700">{f.l}</Label>
                <Input id={f.k} data-testid={`${isReg ? "register" : "login"}-${f.k}-input`} type={f.t || "text"} placeholder={f.ph}
                  required value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                  className="mt-1.5 h-11 rounded-xl focus-visible:ring-cyan-500/40" />
              </div>
            ))}
          </div>
          {!isReg && (
            <div className="-mt-2 text-right">
              <a href={IG_PROFILE} target="_blank" rel="noreferrer" data-testid="forgot-password-link" className="text-sm font-medium text-cyan-700 hover:underline">
                Şifremi unuttum
              </a>
              <p className="text-[11px] text-slate-400">Instagram @kasamnetcom hesabına mesaj atın</p>
            </div>
          )}
          <button disabled={busy} data-testid={isReg ? "register-form-submit-button" : "login-form-submit-button"}
            className="h-11 w-full rounded-xl bg-gradient-to-r from-cyan-600 to-blue-800 font-semibold text-white shadow-sm transition-transform active:scale-[0.98] disabled:opacity-60">
            {busy ? "Lütfen bekleyin…" : isReg ? "Kayıt Ol" : "Giriş Yap"}
          </button>
          <p className="text-center text-sm text-slate-500">
            {isReg ? "Zaten hesabınız var mı? " : "Hesabınız yok mu? "}
            <Link to={isReg ? "/giris" : "/kayit"} data-testid={isReg ? "go-login-link" : "go-register-link"} className="font-semibold text-cyan-700 hover:underline">
              {isReg ? "Giriş yapın" : "Kayıt olun"}
            </Link>
          </p>
          {!navigator.userAgent.includes("Electron") && (
            <a href="/download/KasamNet-Setup.exe" download data-testid="windows-download-link"
              className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50">
              <Monitor className="h-4 w-4 text-cyan-700" /> Windows için indir (Setup)
            </a>
          )}
        </form>
      </div>
    </div>
  );
}
